import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../styles/components/Navbar.module.css";

export default function ChoiceChip({
  label,
  Icon,
  iconRight,
  badge,
  dot = false,
  dotLabel,
  to,
  onClick,
  selected,
  disabled = false,
  type = "button",
  className = "",
  activeMatch = "exact",

  height = 44,
  iconSize = 24,

  selectedColor,
  unselectedColor,
  selectedBackground,
  unselectedBackground,
  fontWeight = 400,
}) {
  const location = useLocation();

  const isActiveByRoute = useMemo(() => {
    if (!to) return false;
    const path = String(location?.pathname || "");
    const target = String(to);

    if (activeMatch === "startsWith") {
      return path === target || path.startsWith(`${target}/`);
    }

    return path === target;
  }, [activeMatch, location?.pathname, to]);

  const isSelected = selected != null ? !!selected : isActiveByRoute;

  const fg = isSelected
    ? selectedColor || "var(--text-cta-primary)"
    : unselectedColor || "var(--text-primary)";

  const bg = isSelected
    ? selectedBackground || "var(--surface-accent-hover)"
    : unselectedBackground || "var(--surface-secondary)";

  const Root = to ? Link : "button";
  const rootProps = to
    ? { to, "aria-current": isSelected ? "page" : undefined }
    : { type, onClick };

  const renderIcon = (Icn, forceSelected) => {
    if (!Icn) return null;

    if (typeof Icn === "string") {
      return (
        <img
          src={Icn}
          alt=""
          aria-hidden="true"
          style={{
            width: iconSize,
            height: iconSize,
            display: "block",
            filter: forceSelected ? "brightness(0) invert(1)" : "none",
          }}
        />
      );
    }

    return (
      <Icn
        aria-hidden="true"
        style={{
          width: iconSize,
          height: iconSize,
          display: "block",
          color: fg,
        }}
      />
    );
  };

  return (
    <Root
      {...rootProps}
      role={to ? undefined : "button"}
      onClick={to ? onClick : onClick}
      aria-pressed={to ? undefined : isSelected}
      aria-disabled={disabled ? "true" : undefined}
      tabIndex={disabled ? -1 : undefined}
      className={className}
      style={{
        height,
        padding: "12px 24px 12px 16px",
        borderRadius: 9999,
        border: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        background: bg,
        opacity: disabled ? 0.6 : 1,
        textDecoration: "none",
        pointerEvents: disabled ? "none" : "auto",
        userSelect: "none",
      }}
    >
      {renderIcon(Icon, isSelected)}

      <span
        style={{
          color: fg,
          fontSize: 13,
          fontFamily: "DM Sans",
          fontWeight,
          lineHeight: "16px",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>

      {badge != null && badge !== false ? (
        <span
          style={{
            marginLeft: 2,
            minWidth: 18,
            height: 18,
            padding: "0 6px",
            borderRadius: 9999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "DM Sans",
            fontSize: 12,
            fontWeight: 600,
            background: isSelected
              ? "rgba(255,255,255,0.22)"
              : "rgba(0,0,0,0.10)",
            color: fg,
          }}
        >
          {badge}
        </span>
      ) : null}

      {dot ? (
        <span
          role={dotLabel ? "img" : undefined}
          aria-label={dotLabel || undefined}
          aria-hidden={dotLabel ? undefined : "true"}
          style={{
            width: 8,
            height: 8,
            flexShrink: 0,
            borderRadius: 9999,
            background: "var(--surface-error)",
          }}
        />
      ) : null}

      {renderIcon(iconRight, isSelected)}
    </Root>
  );
}
